import pluralize from 'pluralize';

import {
  FacilityPayload,
  useFacilitiesQuery
} from '@/hooks/useFacilitiesQuery';
import { useAccessibleFacilityIds } from '@/pages/CRM/Screener/hooks/useInstanceAccessibility';
import { FacilityFacilityTypeEnum } from '@/types/crm-screener';

import { useScreenerFacilityView } from './useScreenerFacilityView';

export interface CareSettingTerminology {
  singularTitle: string;
  pluralTitle: string;
  singularLower: string;
  pluralLower: string;
}

const createCareSettingTerminology = (
  singularTitle: string
): CareSettingTerminology => {
  const singularLower = singularTitle.toLowerCase();

  return {
    singularTitle,
    pluralTitle: pluralize(singularTitle),
    singularLower,
    pluralLower: pluralize(singularLower)
  };
};

const FACILITY_TERMS = createCareSettingTerminology('Facility');
const AGENCY_TERMS = createCareSettingTerminology('Agency');
const HOSPITAL_TERMS = createCareSettingTerminology('Hospital');
const CARE_SETTING_TERMS = createCareSettingTerminology('Care Setting');

const careSettingTerminologyByFacilityType: Partial<
  Record<FacilityFacilityTypeEnum, CareSettingTerminology>
> = {
  [FacilityFacilityTypeEnum.SkilledNursing]: FACILITY_TERMS,
  [FacilityFacilityTypeEnum.CriticalCare]: FACILITY_TERMS,
  [FacilityFacilityTypeEnum.AssistedLiving]: FACILITY_TERMS,
  [FacilityFacilityTypeEnum.HomeHealth]: AGENCY_TERMS,
  [FacilityFacilityTypeEnum.Hospice]: AGENCY_TERMS,
  [FacilityFacilityTypeEnum.LongTermCareHospital]: HOSPITAL_TERMS
};

export const getCareSettingTerminologyForFacilityType = (
  facilityType?: FacilityFacilityTypeEnum | null
): CareSettingTerminology =>
  (facilityType && careSettingTerminologyByFacilityType[facilityType]) ||
  FACILITY_TERMS;

export const getCareSettingTerminologyForFacilityTypes = (
  facilityTypes: Array<FacilityFacilityTypeEnum | undefined | null>
): CareSettingTerminology => {
  const terms = new Map<string, CareSettingTerminology>();

  facilityTypes.forEach((facilityType) => {
    if (!facilityType) {
      return;
    }
    const terminology = getCareSettingTerminologyForFacilityType(facilityType);
    terms.set(terminology.singularTitle, terminology);
  });

  if (terms.size === 0) {
    return FACILITY_TERMS;
  }

  if (terms.size === 1) {
    return Array.from(terms.values())[0] ?? FACILITY_TERMS;
  }

  return CARE_SETTING_TERMS;
};

export const getAccessibleFacilityTypes = (
  facilities: FacilityPayload[],
  accessibleFacilityIds: ReadonlySet<string>
): FacilityFacilityTypeEnum[] => {
  const facilityTypes = new Set<FacilityFacilityTypeEnum>();

  facilities.forEach((facility) => {
    if (facility.facility_type && accessibleFacilityIds.has(facility.id)) {
      facilityTypes.add(facility.facility_type);
    }
  });

  return Array.from(facilityTypes);
};

export const formatCareSettingCount = (
  count: number,
  terminology: CareSettingTerminology
) => pluralize(terminology.singularTitle, count, true);

export const useAccessibleCareSettingTerminology =
  (): CareSettingTerminology => {
    const accessibleFacilityIds = useAccessibleFacilityIds();
    const { data: facilities = [] } = useFacilitiesQuery().useFindAll();

    return getCareSettingTerminologyForFacilityTypes(
      getAccessibleFacilityTypes(facilities, accessibleFacilityIds)
    );
  };

export const useSelectedScreenerCareSettingTerminology =
  (): CareSettingTerminology => {
    const { view } = useScreenerFacilityView();
    return getCareSettingTerminologyForFacilityType(view.facilityType);
  };
